import { useEffect, useState } from "react";
import { RulesModel } from "../account/accountApiSlice";
import ButtonWithForm from "../../components/ButtonWithForm";
import Password from "../../components/input/Password";
import RequiredField from "../../components/RequiredField";
import {
	CourseWritersResetPasswordViewModel,
	useResetStudentPasswordMutation,
} from "./usersApiSlice";

interface ButtonProps {
	studentId: number;
	studentName: string;
	rules: RulesModel;
	setResetPasswordError: (message: string) => void;
}

export default function ResetStudentPasswordButton(props: ButtonProps) {
	const [resetModel, setResetModel] = useState<CourseWritersResetPasswordViewModel>({
		userId: props.studentId,
		password: "",
		confirmPassword: "",
	});
	const [resetPassword, setResetPassword] = useState("");
	const [resetConfirmPassword, setResetConfirmPassword] = useState("");
	const [disableResetPasswordConfirm, setDisableResetPasswordConfirm] = useState(true);

	const [resetStudentPassword] = useResetStudentPasswordMutation();

	useEffect(() => {
		setResetModel({
			...resetModel,
			password: resetPassword,
			confirmPassword: resetConfirmPassword,
		});
	}, [resetPassword, resetConfirmPassword]);

	function handleResetStudentPassword() {
		if (resetModel) {
			resetStudentPassword(resetModel)
				.unwrap()
				.then(() => {
					props.setResetPasswordError("");
				})
				.catch((error) =>
					props.setResetPasswordError(
						error.data
							? `There was an error resetting the password. ${error.data}`
							: "There was an unexpected error while trying to reset the password."
					)
				);
		}
	}

	function updatePassword(password: string) {
		setResetPassword(password);
	}

	function updateConfirmPassword(confirmPassword: string) {
		setResetConfirmPassword(confirmPassword);
	}

	return (
		<ButtonWithForm
			id="reset-password-button"
			className="btn btn-naked border !shadow-md mt-1"
			title={`Reset Password: ${props.studentName}`}
			confirmLabel="Save"
			onConfirm={handleResetStudentPassword}
			disableConfirm={disableResetPasswordConfirm}
			form={
				<ResetStudentPasswordForm
					rules={props.rules}
					updatePassword={updatePassword}
					updateConfirmPassword={updateConfirmPassword}
					disableConfirm={setDisableResetPasswordConfirm}
				/>
			}>
			Reset Password
		</ButtonWithForm>
	);
}

interface FormProps {
	rules: RulesModel;
	updatePassword: (password: string) => void;
	updateConfirmPassword: (confirmPass: string) => void;
	disableConfirm: (disable: boolean) => void;
}

export function ResetStudentPasswordForm(props: FormProps) {
	const minLength = props.rules.writerPasswordMinLength ?? 4;
	const maxLength = props.rules.passwordMaxLength ?? 72;

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	useEffect(() => {
		props.updatePassword(password);
	}, [password]);

	useEffect(() => {
		props.updateConfirmPassword(confirmPassword);
	}, [confirmPassword]);

	useEffect(() => {
		if (!password || !confirmPassword) {
			props.disableConfirm(true);
		} else if (password !== confirmPassword) {
			props.disableConfirm(true);
		} else if (password.length < minLength || password.length > maxLength) {
			props.disableConfirm(true);
		} else {
			props.disableConfirm(false);
		}
	}, [password, confirmPassword]);

	return (
		<div>
			<p className="text-sm leading-6 mt-2">
				Student passwords must be at least {minLength} characters long and are case
				sensitive.
			</p>
			<div className="mt-2">
				<div className="flex justify-between items-end">
					<label htmlFor="password" className="font-semibold text-sm">
						Password
						<RequiredField />
					</label>
					<p className="text-xs leading-6">
						<RequiredField className="!text-base" /> - Denotes a required field
					</p>
				</div>
				<div className="mt-1">
					<Password
						id="passwordInput"
						name="password"
						className="block w-full rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						required={true}
						minLength={minLength}
						maxLength={maxLength}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
					/>
				</div>
			</div>

			<div className="mt-2">
				<label htmlFor="confirmPassword" className="font-semibold text-sm">
					Confirm Password
					<RequiredField />
				</label>
				<div className="mt-1">
					<Password
						id="confirmPasswordInput"
						name="confirmPassword"
						placeholder="confirm password"
						className="block w-full rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						required={true}
						minLength={minLength}
						maxLength={maxLength}
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
					/>
				</div>
			</div>
		</div>
	);
}
